import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  DefaultInfluencerIcon,
  MailIcon,
  ProspectsIcon,
  TrashIcon,
} from '../assets';
import { Tab } from '../components/campaigns/SingleCampaignTabs';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import ExpandableListElement from '../components/common/ExpandableListElement';
import { LateralProfileProspectMenu } from '../components/common/LateralProfileProspectMenu';
import { BrandGroupPopUp } from '../components/popups/BrandGroupPopup';
import { CreateBrandUser } from '../components/popups/CreateBrandUser';
import TeamMember from '../components/users/TeamMember';
import { ROUTES } from '../config/routes/config';
import {
  DELETE_BRAND_GROUP,
  DELETE_BRAND_GROUP_USER,
} from '../graphql/brands/mutation';
import { GET_PROSPECT } from '../graphql/prospects/query';
import { useNotification } from '../hooks/useNotification';
import { usePermissions } from '../hooks/usePermissions';
import type { BrandGroup, SingleUser } from '../types';

export const SingleProspectMembers = () => {
  const { id } = useParams();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { notifyWarning } = useNotification();
  const { isInfluentia } = usePermissions();
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<null | BrandGroup | true>(null);

  const { data, refetch } = useQuery(GET_PROSPECT, {
    variables: { brandId: id },
    onError: () => {
      notifyWarning(t('global.errorSingleBrand'));
      navigate(ROUTES.PROSPECTS);
    },
    skip: !id,
  });

  const brand = data?.brand?.data;

  const [deleteBrandGroup] = useMutation(DELETE_BRAND_GROUP);
  const onDeleteBrandGroup = (groupId: string) => {
    deleteBrandGroup({
      variables: { id: groupId },
      onCompleted: () => refetch(),
    });
  };

  const [deleteBrandGroupUser] = useMutation(DELETE_BRAND_GROUP_USER);
  const onDeleteBrandGroupUser = (group_id: string, user_id: string) => {
    deleteBrandGroupUser({
      variables: { group_id, user_id },
      onCompleted: () => refetch(),
    });
  };

  return (
    <div className="w-full flex flex-col gap-y-5">
      {brand && (
        <>
          <Breadcrumbs
            icon={<ProspectsIcon />}
            breadcrumbs={[
              { text: t('prospects.title'), link: ROUTES.PROSPECTS },
              {
                text: brand.name,
                link: ROUTES.SINGLE_PROSPECT.replace(':id', brand.id),
              },
              { text: t('profile.menu.members') },
            ]}
          />
          <div className="flex-row gap-x-2 overflow-x-auto customScroll pb-1 lg:hidden flex">
            <Tab
              to={ROUTES.SINGLE_PROSPECT.replace(':id', brand.id)}
              text={t('profile.menu.title1')}
            />
            <Tab
              to={ROUTES.SINGLE_PROSPECT_MEMBERS.replace(':id', brand.id)}
              text={t('profile.menu.members')}
            />
            <Tab
              to={ROUTES.SINGLE_PROSPECT_CAMPAIGNS.replace(':id', brand.id)}
              text={t('profile.menu.title2')}
            />
          </div>
          {openGroup && (
            <BrandGroupPopUp
              close={() => setOpenGroup(null)}
              refetch={refetch}
              id={openGroup !== true ? openGroup.id : undefined}
              name={openGroup !== true ? openGroup.name : undefined}
              users={openGroup !== true ? openGroup.users : undefined}
              brand_id={brand.id}
            />
          )}
          <div className="flex flex-row lg:gap-12 h-full">
            <LateralProfileProspectMenu brand={brand} />
            <div className="flex flex-col w-full gap-8">
              {brand.users && (
                <div>
                  <span className="uppercase text-[16px]">
                    {t('singleBrand.team')}
                  </span>
                  <div className="mt-2 flex flex-col gap-2 w-full md:overflow-auto overflow-scroll">
                    {brand.users.map((u: any) => (
                      <TeamMember key={u.id} user={u} refetch={refetch} />
                    ))}
                  </div>
                  <div className="flex items-center justify-center mt-2">
                    <button onClick={() => setOpen(true)}>
                      <span className="font-medium text-xs underline">
                        {t('brands.addUser')}
                      </span>
                    </button>
                  </div>
                  <CreateBrandUser
                    setOpen={setOpen}
                    open={open}
                    brandId={brand.id}
                    refetch={refetch}
                  />
                </div>
              )}

              {isInfluentia() && (
                <div>
                  <span className="uppercase text-[16px]">
                    {t('singleBrand.groups')}
                  </span>
                  <div className="flex flex-col gap-2 w-full md:overflow-auto overflow-scroll mt-2">
                    {brand.groups?.map((bg: BrandGroup) => (
                      <ExpandableListElement
                        key={bg.id}
                        editFunction={() => setOpenGroup(bg)}
                        title={bg.name}
                        noSubrowsText={t('singleBrand.noUsersGroup')}
                        deleteFunction={() => onDeleteBrandGroup(bg.id)}
                        SubrowComponent={({
                          name,
                          email,
                          profile_picture,
                          id: userId,
                        }: SingleUser) => (
                          <>
                            <div className="flex flex-row gap-5 items-center sm:min-w-[200px]">
                              <div>
                                {!profile_picture ? (
                                  <DefaultInfluencerIcon className="size-[50px]" />
                                ) : (
                                  <img
                                    src={`${process.env.REACT_APP_AWS_PUBLIC_ENDPOINT}/${profile_picture}`}
                                    className="rounded-full size-[50px] object-cover"
                                    alt=""
                                  />
                                )}
                              </div>
                              <span className="font-bold min-w-[150px]">
                                {name}
                              </span>
                            </div>
                            <div className="flex flex-row gap-1 items-center min-w-[270px]">
                              <MailIcon className="size-[14px]" />
                              <span className="font-medium">{email}</span>
                            </div>
                            <div className="flex items-center">
                              <button
                                onClick={() =>
                                  onDeleteBrandGroupUser(bg.id, userId)
                                }
                              >
                                <TrashIcon className="size-[24px]" />
                              </button>
                            </div>
                          </>
                        )}
                        subrowElements={bg.users.map((u) =>
                          brand.users.find((u2: any) => u2.id === u),
                        )}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-center mt-2">
                    <button onClick={() => setOpenGroup(true)}>
                      <span className="font-medium text-xs underline">
                        {t('brands.addGroup')}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
